import { useState } from "react";
import { fetchTherapistForClient } from "../../../Services/apiService";

const formatTherapists = (therapists) =>
  (therapists || []).map((t) => ({
    id: t.therapist_id || t.id || "",
    name: t.therapist_name || t.name || "",
    practice: t.practice_name || t.practice || "",
    city: t.city || "",
    email: t.therapist_email || t.email || "",
  }));

const matches = (th, q) =>
  (th.name || "").toLowerCase().includes(q) ||
  (th.practice || "").toLowerCase().includes(q) ||
  (th.city || "").toLowerCase().includes(q);

export const useTherapist = ({ setFormData, t }) => {
  const [therapistOptions, setTherapistOptions] = useState([]);
  const [filteredTherapists, setFilteredTherapists] = useState([]);
  const [showTherapistSuggestions, setShowTherapistSuggestions] = useState(false);
  const [loadingTherapists, setLoadingTherapists] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreTherapists, setHasMoreTherapists] = useState(true);

  const loadInitialTherapists = async () => {
    try {
      setLoadingTherapists(true);
      const data = await fetchTherapistForClient(1, 100);
      const formatted = formatTherapists(data.therapists);
      setTherapistOptions(formatted);
      setCurrentPage(1);
      setHasMoreTherapists(formatted.length >= 100 && (data.total || 0) > 100);
    } catch {
      setTherapistOptions([]);
      setHasMoreTherapists(false);
    } finally {
      setLoadingTherapists(false);
    }
  };

  const loadMoreTherapists = async (searchQuery = "") => {
    if (!hasMoreTherapists || isSearching) return [];

    try {
      setIsSearching(true);
      const nextPage = currentPage + 1;
      const data = await fetchTherapistForClient(nextPage, 100);
      const newTherapists = formatTherapists(data.therapists);

      if (newTherapists.length > 0) {
        setTherapistOptions((prev) => {
          const merged = [...prev, ...newTherapists];
          setHasMoreTherapists(
            newTherapists.length >= 100 && merged.length < (data.total || 0)
          );
          return merged;
        });
        setCurrentPage(nextPage);

        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          return newTherapists.filter((th) => matches(th, q));
        }
        return newTherapists;
      }

      setHasMoreTherapists(false);
      return [];
    } catch {
      setHasMoreTherapists(false);
      return [];
    } finally {
      setIsSearching(false);
    }
  };

  const handleTherapistSearch = async (value) => {
    setFormData((prev) => ({ ...prev, therapistSearch: value }));

    if (loadingTherapists) return;

    if (value.length > 0) {
      const q = value.toLowerCase();
      let filtered = therapistOptions.filter((th) => matches(th, q));

      if (filtered.length === 0 && hasMoreTherapists && !isSearching) {
        let found = [];
        let attempts = 0;
        while (found.length === 0 && hasMoreTherapists && attempts < 10) {
          attempts++;
          const newMatches = await loadMoreTherapists(value);
          found = [...found, ...newMatches];
          // Also re-filter against all loaded options
          const allFiltered = therapistOptions.filter((th) => matches(th, q));
          if (allFiltered.length > 0) { found = [...found, ...allFiltered]; break; }
        }
        if (found.length > 0) {
          filtered = found.filter((th, idx, self) => idx === self.findIndex((x) => x.id === th.id));
        }
      }

      setFilteredTherapists(filtered);
      setShowTherapistSuggestions(true);
    } else {
      setFilteredTherapists([]);
      setShowTherapistSuggestions(false);
    }
  };

  const handleTherapistSelection = (therapist) => {
    setFormData((prev) => ({
      ...prev,
      therapistSearch: `${therapist.name} - ${therapist.practice}, ${therapist.city}`,
      selectedTherapist: therapist.id,
      otherTherapist: false,
      selectedTherapistData: {
        id: therapist.id,
        name: therapist.name,
        practice: therapist.practice,
        city: therapist.city,
        email: therapist.email,
      },
    }));
    setShowTherapistSuggestions(false);
  };

  const handleCustomTherapist = () => {
    setFormData((prev) => ({
      ...prev,
      therapistSearch: t.otherNotListed,
      selectedTherapist: "other",
      otherTherapist: true,
    }));
    setShowTherapistSuggestions(false);
  };

  return {
    therapistOptions,
    filteredTherapists,
    showTherapistSuggestions,
    setShowTherapistSuggestions,
    loadingTherapists,
    isSearching,
    loadInitialTherapists,
    handleTherapistSearch,
    handleTherapistSelection,
    handleCustomTherapist,
  };
};
